import React, { useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useResearchDemo } from '../../contexts/ResearchDemoContext';
import type { Claim } from '../../types/researchDemo';
import { formatTimestamp } from '../../utils/demoStorage';
import { glassPanel, linkButton, secondaryButton } from '../../utils/demoUi';
import { claimSummary, outcomeLabels, passageFor, sourceById, statusLabels } from '../../utils/researchDemo';
import { ReviewForm } from './ReviewForm';
import { StatusPill } from './StatusPill';
export function EvidenceRecord({
  claim
}: {
  claim: Claim;
}) {
  const {
    state,
    dispatch,
    inspectSource
  } = useResearchDemo();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const source = sourceById(claim.sourceId);
  const passage = passageFor(claim.sourceId, claim.passageId);
  const canReview = state.ui.workspace === 'curation';
  useEffect(() => {
    window.scrollTo(0, 0);
    headingRef.current?.focus();
  }, [claim.id]);
  return <div data-id="element-2087" data-mp="10gdewqikeoh0">
      <button type="button" onClick={() => dispatch({
      type: 'openClaim',
      id: null
    })} className={secondaryButton} data-id="element-2088" data-mp="10g4y4njbmz0j">
        <ArrowLeft size={16} aria-hidden="true" data-id="element-2089" data-mp="vi3jcesxhluv" />
        Back
      </button>

      <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-2090" data-mp="19gtg1tbu5jip">
        <p className="font-mono text-sm font-semibold text-ink-muted" data-id="element-2091" data-mp="19ie6p7v14iba">
          Evidence record · {claim.id} · version {claim.version}
        </p>
        <StatusPill status={claim.status} data-id="element-2092" data-mp="1jeplc4i3dk8w" />
      </div>
      <h1 ref={headingRef} tabIndex={-1} className="mt-2 font-heading text-3xl font-bold leading-tight tracking-tight text-ink-dark outline-none" data-id="element-2093" data-mp="1jd67ekjwrlaa">
        {claimSummary(claim)}
      </h1>

      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-2" data-id="element-2094" data-mp="1t497bj13pu7z">
        <section aria-labelledby="interpretation-title" className={`${glassPanel} p-6`} data-id="element-2095" data-mp="1o70mtbshde3k">
          <h2 id="interpretation-title" className="text-sm font-semibold text-ink-muted" data-id="element-2096" data-mp="224z1kiaprcjl">
            Reviewer interpretation · current
          </h2>
          <p className="mt-2 text-lg leading-relaxed text-ink-dark" data-id="element-2097" data-mp="1x4kscwxdlwjz">{claim.interpretation}</p>
          <h2 className="mt-6 text-sm font-semibold text-ink-muted" data-id="element-2098" data-mp="ur4zw694ca9c">Scope and limitations</h2>
          <p className="mt-2 leading-relaxed text-ink-dark" data-id="element-2099" data-mp="zskkjx7dwer7">{claim.limitation}</p>
        </section>

        <section aria-labelledby="passage-title" className={`${glassPanel} p-6`} data-id="element-2100" data-mp="13smq4hg8psbr">
          <h2 id="passage-title" className="text-sm font-semibold text-ink-muted" data-id="element-2101" data-mp="18m7edhs177xr">
            Exact supporting passage · source text
          </h2>
          {passage && source ? <>
              <blockquote className="mt-3 border-l-4 border-navy/40 pl-4 font-serif text-[1.05rem] leading-relaxed text-ink-dark" data-id="element-2102" data-mp="1ih92caakm3yr">
                “{passage.text}”
              </blockquote>
              <p className="mt-3 text-sm text-ink-muted" data-id="element-2103" data-mp="1ri7b15vyezno">
                {source.id} · {passage.id} · {source.title} ({source.design})
              </p>
              <button type="button" onClick={() => inspectSource(source.id, passage.id)} className={`mt-4 ${linkButton}`} data-id="element-2104" data-mp="3tavcpasy9f1c">
                Open full source context
                <ArrowRight size={14} aria-hidden="true" data-id="element-2105" data-mp="3lsnk82o54x1x" />
              </button>
            </> : <p className="mt-2 text-sm text-rose-700" data-id="element-2106" data-mp="3gyjbbdvbeey7">The cited passage could not be found.</p>}
        </section>
      </div>

      {canReview && <div className="mt-8" data-id="element-2107" data-mp="380z6xw25o2jd">
          <ReviewForm claim={claim} data-id="element-2108" data-mp="37xuvcdmd24io" />
        </div>}

      <section aria-labelledby="versions-title" className="mt-10" data-id="element-2109" data-mp="8jrhk0rd3eep">
        <h2 id="versions-title" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2110" data-mp="3lpwjaadl50l">
          Previous versions
        </h2>
        {claim.previousVersions.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-2111" data-mp="3vrd1bt87qdh4">This is the first version of the record.</p> : <ol className="mt-3 space-y-3" data-id="element-2112" data-mp="2kadcx8ozkisd">
            {[...claim.previousVersions].reverse().map(version => <li key={version.version} className="rounded-xl border border-line bg-white/70 p-5" data-id="element-2113" data-mp="2faqy4utieuhg">
                <p className="text-xs font-semibold text-ink-muted" data-id="element-2114" data-mp="722bp7n9m2zj">
                  Version {version.version} · {statusLabels[version.status]} · {formatTimestamp(version.recordedAt)}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-ink-dark" data-id="element-2115" data-mp="3u7tiq9300g8y">{version.interpretation}</p>
                <p className="mt-1 text-sm leading-relaxed text-ink-muted" data-id="element-2116" data-mp="22pq0q84r1yj">Limitation: {version.limitation}</p>
              </li>)}
          </ol>}
      </section>

      <section aria-labelledby="reviews-title" className="mt-10" data-id="element-2117" data-mp="urwlfgcafbfk">
        <h2 id="reviews-title" className="font-heading text-lg font-bold text-ink-dark" data-id="element-2118" data-mp="uq2c6zadp7rb">
          Review history
        </h2>
        {claim.reviews.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-2119" data-mp="lq5hoh1b5eoc">
            {claim.status === 'approved' ? 'Sample record seeded as approved; no reviews recorded in this browser yet.' : 'No reviews recorded yet.'}
          </p> : <div className={`${glassPanel} mt-3 overflow-x-auto`} data-id="element-2120" data-mp="36fmjn25w1v5u">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-2121" data-mp="2xzdr771p6d8c">
              <caption className="sr-only" data-id="element-2122" data-mp="2tzmvqq1878xh">Reviews of {claim.id}</caption>
              <thead data-id="element-2123" data-mp="37yuw3m94kn39">
                <tr className="border-b border-line text-xs text-ink-muted" data-id="element-2124" data-mp="37zedn1uv59gr">
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-2125" data-mp="32zs1kvqrtz9j">When</th>
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-2126" data-mp="32xp1fivwmpr3">Change</th>
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-2127" data-mp="2faqmipnfnpva">Outcome</th>
                  <th scope="col" className="px-4 py-3 font-semibold" data-id="element-2128" data-mp="2ac5q71baxqc4">Rationale</th>
                </tr>
              </thead>
              <tbody data-id="element-2129" data-mp="2p1blie70qweq">
                {[...claim.reviews].reverse().map(review => <tr key={review.id} className="border-b border-line align-top last:border-b-0" data-id="element-2130" data-mp="2k7pildqnq6w4">
                    <td className="px-4 py-3 text-ink-muted whitespace-nowrap" data-id="element-2131" data-mp="1wi4ygrirp3l9">{formatTimestamp(review.at)}</td>
                    <td className="px-4 py-3 text-ink-dark" data-id="element-2132" data-mp="1rkcvn2gl3x2a">
                      v{review.priorVersion} ({statusLabels[review.priorStatus]}) → v{review.newVersion}
                      <span className="mt-1 block text-xs text-ink-muted" data-id="element-2133" data-mp="fjefynoltb0">Prior interpretation: {review.priorInterpretation}</span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-ink-dark whitespace-nowrap" data-id="element-2134" data-mp="98vckl0oyfre">{outcomeLabels[review.outcome]}</td>
                    <td className="px-4 py-3 leading-relaxed text-ink-dark" data-id="element-2135" data-mp="38orzcjts026j">{review.rationale}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </section>
    </div>;
}